import { useRef, useState, type FormEvent } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ApiError } from '../../api/client'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { EmptyState } from '../../components/ui/empty-state'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'
import { customEmojiQuery, invalidateCustomEmoji } from '../../lib/custom-emoji'
import { SmilePlus } from 'lucide-react'

// DIS-64: Settings › Emoji (admin): upload PNG/GIF/WebP ≤ 128 KB, ≤ 128×128; delete.
export const Route = createFileRoute('/_app/settings/emoji')({
  beforeLoad: ({ context }) => {
    if (context.me.role !== 'admin') throw redirect({ to: '/' })
  },
  component: EmojiPage,
})

function EmojiPage() {
  const qc = useQueryClient()
  const list = useQuery(customEmojiQuery)
  const [error, setError] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const form = useRef<HTMLFormElement>(null)
  const upload = useMutation({
    mutationFn: async (fd: FormData) => {
      const res = await fetch('/api/admin/emoji', { method: 'POST', body: fd, credentials: 'same-origin' })
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as ApiError
        throw new Error(err.fields?.name ?? err.fields?.file ?? err.message ?? `upload failed (${res.status})`)
      }
      return res.json()
    },
    onSuccess: async () => {
      setError(null)
      form.current?.reset()
      await invalidateCustomEmoji(qc)
    },
    onError: (e: Error) => setError(e.message),
  })
  const remove = useMutation({
    mutationFn: async (name: string) => {
      await api.DELETE('/admin/emoji/{name}', { params: { path: { name } } })
    },
    onSuccess: () => invalidateCustomEmoji(qc),
  })
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    upload.mutate(new FormData(e.currentTarget))
  }
  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-lg font-semibold">Custom emoji</h1>
        <p className="text-[13px] text-fg-muted">Type <code className="rounded bg-bg-muted px-1">:name:</code> in a message or pick them from the reaction picker. PNG, GIF or WebP, up to 128 KB and 128×128.</p>
      </div>
      <form ref={form} onSubmit={submit} className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-bg-muted p-4" data-testid="emoji-form">
        <label className="block text-[13px]">
          <span className="font-medium">Name</span>
          <Input name="name" required pattern="[a-z0-9_\-]{2,32}" placeholder="partyparrot" className="mt-1 w-48" aria-label="Emoji name" />
        </label>
        <label className="block text-[13px]">
          <span className="font-medium">Image</span>
          <input name="file" type="file" accept="image/png,image/gif,image/webp" required aria-label="Emoji image" className="mt-1 block text-[12px]" />
        </label>
        <Button type="submit" disabled={upload.isPending}>{upload.isPending ? 'Uploading…' : 'Add emoji'}</Button>
        {error && (
          <p role="alert" className="basis-full text-[13px] text-danger">
            {error}
          </p>
        )}
      </form>
      {list.isSuccess && list.data.length === 0 && <EmptyState icon={<SmilePlus size={16} />} title="No custom emoji yet" hint="Add one above; everyone can use it right away." />}
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="emoji-list">
        {list.data?.map((e) => (
          <li key={e.name} className="flex items-center gap-2 rounded-md border border-border p-2 text-[13px]" data-testid="emoji-item" data-name={e.name}>
            <img src={e.url} alt={`:${e.name}:`} className="size-8 object-contain" />
            <span className="min-w-0 flex-1 truncate font-mono text-[12px]">:{e.name}:</span>
            <Button size="sm" variant="ghost" className="text-danger" onClick={() => setRemoving(e.name)} aria-label={`Delete ${e.name}`}>
              Delete
            </Button>
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={`Delete :${removing}:?`}
        description="Existing reactions keep the name; the picture goes away."
        confirmLabel="Delete"
        onConfirm={() => {
          if (removing) remove.mutate(removing)
          setRemoving(null)
        }}
      />
    </div>
  )
}
